import {
  generateHeartProvinceTopology,
  selectHeartProvinceFront,
  type HeartProvinceCell,
  type HeartProvinceFront,
  type HeartProvinceTopology
} from "./heart-province-topology";

export const HEART_FRONT_DEFINITION_SCHEMA =
  "vaelor-heart-front-definition/v1" as const;

export type HeartFrontNumber = HeartProvinceFront;

export type HeartFrontId =
  | "heart-front-1"
  | "heart-front-2"
  | "heart-front-3"
  | "heart-front-4"
  | "heart-front-5"
  | "heart-front-6";

export interface HeartFrontDefinition {
  readonly schema: typeof HEART_FRONT_DEFINITION_SCHEMA;
  readonly frontId: HeartFrontId;
  readonly front: HeartFrontNumber;
  readonly arrivalId: string;
  readonly clearingIds: readonly [string, string, string, string];
  readonly pillarIds: {
    readonly left: string;
    readonly right: string;
  };
  readonly guardianId: string;
  readonly gateId: string;
  readonly citadelId: string;
}

const HEART_FRONT_IDS = Object.freeze([
  "heart-front-1",
  "heart-front-2",
  "heart-front-3",
  "heart-front-4",
  "heart-front-5",
  "heart-front-6"
] as const);

const CLEARING_SLOTS = Object.freeze([0, 2, 5, 7] as const);
const PILLAR_SLOTS = Object.freeze([1, 3] as const);

function frontIdFor(front: HeartFrontNumber): HeartFrontId {
  return HEART_FRONT_IDS[front - 1];
}

function requiredCell(
  cells: readonly HeartProvinceCell[],
  role: HeartProvinceCell["role"],
  slot: number
): HeartProvinceCell {
  const cell = cells.find((candidate) =>
    candidate.role === role && candidate.slot === slot
  );
  if (cell === undefined) throw new Error("heart_front_definition_registry_invalid");
  return cell;
}

function buildDefinition(
  topology: HeartProvinceTopology,
  front: HeartFrontNumber
): HeartFrontDefinition {
  const cells = selectHeartProvinceFront(topology, front);
  const arrival = requiredCell(cells, "arrival", 6);
  const clearingIds = Object.freeze([
    requiredCell(cells, "camp", CLEARING_SLOTS[0]).id,
    requiredCell(cells, "camp", CLEARING_SLOTS[1]).id,
    requiredCell(cells, "camp", CLEARING_SLOTS[2]).id,
    requiredCell(cells, "camp", CLEARING_SLOTS[3]).id
  ] as [string, string, string, string]);
  const pillarIds = Object.freeze({
    left: requiredCell(cells, "pillar", PILLAR_SLOTS[0]).id,
    right: requiredCell(cells, "pillar", PILLAR_SLOTS[1]).id
  });

  return Object.freeze({
    schema: HEART_FRONT_DEFINITION_SCHEMA,
    frontId: frontIdFor(front),
    front,
    arrivalId: arrival.id,
    clearingIds,
    pillarIds,
    guardianId: requiredCell(cells, "guardian", 0).id,
    gateId: requiredCell(cells, "gate", 0).id,
    citadelId: requiredCell(cells, "citadel", 0).id
  });
}

function buildDefinitions(
  topology: HeartProvinceTopology
): readonly [
  HeartFrontDefinition,
  HeartFrontDefinition,
  HeartFrontDefinition,
  HeartFrontDefinition,
  HeartFrontDefinition,
  HeartFrontDefinition
] {
  return Object.freeze([
    buildDefinition(topology, 1),
    buildDefinition(topology, 2),
    buildDefinition(topology, 3),
    buildDefinition(topology, 4),
    buildDefinition(topology, 5),
    buildDefinition(topology, 6)
  ]);
}

export function generateHeartFrontDefinitions(): readonly [
  HeartFrontDefinition,
  HeartFrontDefinition,
  HeartFrontDefinition,
  HeartFrontDefinition,
  HeartFrontDefinition,
  HeartFrontDefinition
] {
  return buildDefinitions(generateHeartProvinceTopology());
}

export const HEART_FRONT_DEFINITIONS = generateHeartFrontDefinitions();

export const HEART_FRONT_1_DEFINITION = HEART_FRONT_DEFINITIONS[0];
export const HEART_FRONT_2_DEFINITION = HEART_FRONT_DEFINITIONS[1];
export const HEART_FRONT_3_DEFINITION = HEART_FRONT_DEFINITIONS[2];
export const HEART_FRONT_4_DEFINITION = HEART_FRONT_DEFINITIONS[3];
export const HEART_FRONT_5_DEFINITION = HEART_FRONT_DEFINITIONS[4];
export const HEART_FRONT_6_DEFINITION = HEART_FRONT_DEFINITIONS[5];

export function isHeartFrontId(value: unknown): value is HeartFrontId {
  return typeof value === "string" && HEART_FRONT_IDS.includes(value as HeartFrontId);
}

export function heartFrontDefinition(
  value: unknown
): HeartFrontDefinition | undefined {
  if (typeof value === "number") {
    if (!Number.isInteger(value) || value < 1 || value > 6) return undefined;
    return HEART_FRONT_DEFINITIONS[value - 1];
  }
  if (!isHeartFrontId(value)) return undefined;
  return HEART_FRONT_DEFINITIONS[HEART_FRONT_IDS.indexOf(value)];
}

export function heartFrontClearingForPlayerId(
  frontId: HeartFrontId,
  playerId: string
): string | undefined {
  const definition = heartFrontDefinition(frontId);
  if (!definition || playerId.length === 0) return undefined;
  let hash = 0x811c9dc5;
  for (let index = 0; index < playerId.length; index += 1) {
    hash ^= playerId.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return definition.clearingIds[hash % definition.clearingIds.length];
}

export const heartFrontDefinitionById = heartFrontDefinition;
export const getHeartFrontDefinition = heartFrontDefinition;

function serializableDefinition(definition: HeartFrontDefinition) {
  return {
    schema: definition.schema,
    frontId: definition.frontId,
    front: definition.front,
    arrivalId: definition.arrivalId,
    clearingIds: definition.clearingIds,
    pillarIds: {
      left: definition.pillarIds.left,
      right: definition.pillarIds.right
    },
    guardianId: definition.guardianId,
    gateId: definition.gateId,
    citadelId: definition.citadelId
  };
}

export function serializeHeartFrontDefinition(
  definition: HeartFrontDefinition
): string {
  return JSON.stringify(serializableDefinition(definition));
}

export function serializeHeartFrontDefinitions(
  definitions: readonly HeartFrontDefinition[] = HEART_FRONT_DEFINITIONS
): string {
  return JSON.stringify(definitions.map(serializableDefinition));
}
